import { errorHandler } from '@/composables/errorHandler'
import { useCooperantOrdersStore } from '@/stores/orders/cooperantOrders'
import { useEcommerceOrdersStore } from '@/stores/orders/ecommerceOrders'
import { createPrefetchGuard } from '@/utils/routerGuards'
import Orders from '@/views/Orders/Index.vue'
import InternalOrdersProduct from '@/views/Orders/InternalOrders/Product.vue'

export default [
    {
        path: '/orders',
        name: 'orders',
        component: Orders,
        meta: {
            title: 'orders.pageTitle',
            menuRendered: true,
            menuTitle: 'orders.menuTitle',
            icon: 'mdi-cart-outline'
        },
        children: [
            {
                path: '/orders/internal-orders',
                name: 'ordersInternalOrders',
                component: () => import('@/views/Orders/InternalOrders/Index.vue'),
                meta: {
                    title: 'orders.internalOrders.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.internalOrders.menuTitle',
                    requiresAuth: true,
                    permission: 'orders'
                }
            },
            {
                path: '/orders/internal-orders/create',
                name: 'ordersInternalOrdersOrderCreate',
                component: () => import('@/views/Orders/InternalOrders/OrderCreate.vue'),
                meta: {
                    title: 'orders.internalOrders.createPageTitle',
                    requiresAuth: true,
                    permission: 'orders_create'
                }
            },
            {
                path: '/orders/internal-orders/:id',
                name: 'ordersInternalOrdersOrder',
                component: () => import('@/views/Orders/InternalOrders/Order.vue'),
                meta: {
                    title: 'orders.internalOrders.orderPageTitle',
                    requiresAuth: true,
                    permission: 'orders_read'
                }
            },
            {
                path: '/orders/internal-orders/create/add-product',
                name: 'ordersInternalOrdersAddProduct',
                component: InternalOrdersProduct,
                meta: {
                    title: 'orders.internalOrders.addProductPageTitle',
                    requiresAuth: true,
                    permission: 'orders_create'
                }
            },
            {
                path: '/orders/internal-orders/create/edit-product/:index',
                name: 'ordersInternalOrdersEditProduct',
                component: InternalOrdersProduct,
                meta: {
                    title: 'orders.internalOrders.editProductPageTitle',
                    requiresAuth: true,
                    permission: 'orders_update'
                }
            },
            {
                path: '/orders/ecommerce-orders',
                name: 'ordersEcommerceOrders',
                component: () => import('@/views/Orders/EcommerceOrders/Index.vue'),
                meta: {
                    title: 'orders.ecommerce.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.ecommerce.menuTitle',
                    requiresAuth: true,
                    permission: 'orders'
                }
            },
            {
                path: '/orders/ecommerce-orders/:id',
                name: 'ordersEcommerceOrdersOrder',
                component: () => import('@/views/Orders/EcommerceOrders/Order.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to) => to.params?.id,
                    fetch: async (id) => {
                        const ecommerceOrdersStore = useEcommerceOrdersStore()
                        await Promise.all([
                            ecommerceOrdersStore.fetchOrder(id),
                            ecommerceOrdersStore.fetchPrinters(),
                            ecommerceOrdersStore.fetchCountries()
                        ])
                    },
                    onMissingId: () => ({ name: 'ordersEcommerceOrders' }),
                    onError: () => ({ name: 'ordersEcommerceOrders' })
                }),
                meta: {
                    title: 'orders.ecommerce.orderPageTitle',
                    requiresAuth: true,
                    permission: 'orders_read'
                }
            },
            {
                path: '/orders/nonstandard-orders',
                name: 'ordersNonStandardOrders',
                component: () => import('@/views/Orders/NonStandardOrders/Index.vue'),
                meta: {
                    title: 'orders.nonStandard.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.nonStandard.menuTitle',
                    requiresAuth: true,
                    permission: 'orders'
                }
            },
            {
                path: '/orders/cooperant-orders',
                name: 'ordersCooperantOrders',
                component: () => import('@/views/Orders/CooperantOrders/Index.vue'),
                meta: {
                    title: 'orders.cooperant.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.cooperant.menuTitle',
                    requiresAuth: true,
                    permission: 'cooperant_orders'
                }
            },
            {
                path: '/orders/wmd-orders',
                name: 'ordersWMDOrders',
                component: () => import('@/views/Orders/WMDOrders/Index.vue'),
                meta: {
                    title: 'orders.wmd.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.wmd.menuTitle',
                    requiresAuth: true,
                    permission: 'wmd_orders'
                }
            },
            {
                path: '/orders/wmd-orders/:id',
                name: 'ordersWMDOrdersOrder',
                component: () => import('@/views/Orders/WMDOrders/Order.vue'),
                meta: {
                    title: 'orders.wmd.orderPageTitle',
                    requiresAuth: true,
                    permission: 'wmd_orders_read'
                }
            },
            {
                path: '/orders/cooperant-orders/:id',
                name: 'ordersCooperantOrdersOrder',
                component: () => import('@/views/Orders/CooperantOrders/Order.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to) => to.params?.id,
                    fetch: async (id) => {
                        const cooperantOrdersStore = useCooperantOrdersStore()
                        cooperantOrdersStore.fetchCooperantDeliveryStatusNames().catch(errorHandler)
                        await cooperantOrdersStore.fetchOrder(id)
                    },
                    loader: 'circular',
                    onMissingId: () => ({ name: 'ordersCooperantOrders' }),
                    onError: () => ({ name: 'ordersCooperantOrders' })
                }),
                meta: {
                    title: 'orders.cooperant.orderPageTitle',
                    requiresAuth: true,
                    permission: 'cooperant_orders_read'
                }
            },
            {
                path: '/orders/product-templates',
                name: 'ordersProductTemplates',
                component: () => import('@/views/Orders/ProductTemplates/Index.vue'),
                meta: {
                    title: 'orders.productTemplates.pageTitle',
                    menuRendered: true,
                    menuTitle: 'orders.productTemplates.menuTitle',
                    requiresAuth: true,
                    permission: 'product_templates'
                }
            },
            {
                path: '/orders/product-templates/create',
                name: 'ordersProductTemplatesProductTemplateCreate',
                component: () => import('@/views/Orders/ProductTemplates/ProductTemplate.vue'),
                meta: {
                    title: 'orders.productTemplates.createPageTitle',
                    requiresAuth: true,
                    permission: 'product_templates_create'
                }
            },
            {
                path: '/orders/product-templates/edit/:id',
                name: 'ordersProductTemplatesProductTemplateEdit',
                component: () => import('@/views/Orders/ProductTemplates/ProductTemplate.vue'),
                meta: {
                    title: 'orders.productTemplates.editPageTitle',
                    requiresAuth: true,
                    permission: 'product_templates_update'
                }
            }
        ]
    }
]
